/**
 * Banner resolution for the casino lobby (AMGP-3866).
 *
 * Casino banners may carry a `casinoCategoryId` (a `casino.tenant_categories.id`). A banner without
 * it belongs to the Default set. A category page shows its own banners when at least one of them is
 * eligible for display, otherwise the Default set; the two sets are never combined.
 */
/**
 * @typedef {Object} BannerTexts
 * @property {string} [bannerHeading]
 * @property {string} [bannerDesc]
 * @property {string} [bannerFooter]
 * @property {string} [bannerloggedInBtn]
 * @property {string} [bannerloggedOutBtn]
 */

/**
 * @typedef {Object} Banner
 * @property {BannerTexts} [EN] - Texts per language code; `EN` is the fallback language.
 * @property {string|null} [image] - Desktop image path relative to the S3 host.
 * @property {string|null} [mobileImage] - Mobile image path relative to the S3 host.
 * @property {string|null} [background] - Desktop background path.
 * @property {string|null} [mobileBackground] - Mobile background path.
 * @property {number|string|null} [casinoCategoryId] - Assigned category id; absent for Default.
 */

/**
 * @typedef {Object.<string, {banners: Banner[]}>} BannerSections
 * Banner sections keyed by upper-case page key (`HOME`, `CASINO`, ...), as served by the casino
 * category endpoint under `bannerUrl`.
 */

/** The only page type whose banners are resolved per main casino category. */
export const CATEGORY_BANNER_PAGE = 'casino'

/**
 * Builds the full URL of a banner image or background for the current viewport.
 * Prefers the mobile variant (`mobileImage`, `mobileBackground`) on mobile when it exists,
 * otherwise uses the desktop asset.
 *
 * @param {Banner} banner - The banner to read from.
 * @param {'image'|'background'} assetName - Which asset to resolve.
 * @param {boolean} isMobile - `true` when the viewport is at or below the mobile breakpoint.
 * @returns {string} Absolute URL prefixed with `REACT_APP_S3_IMAGE_HEADER`, or an empty string.
 */
export const getBannerAsset = (banner, assetName, isMobile) => {
  const mobileAsset = banner?.[`mobile${assetName.charAt(0).toUpperCase()}${assetName.slice(1)}`]
  const desktopAsset = banner?.[assetName]
  const selectedAsset = isMobile && mobileAsset ? mobileAsset : desktopAsset

  return selectedAsset ? process.env.REACT_APP_S3_IMAGE_HEADER + selectedAsset : ''
}

const TEXT_FIELDS = ['bannerHeading', 'bannerloggedInBtn', 'bannerloggedOutBtn', 'bannerDesc', 'bannerFooter']

/**
 * Reads one text field of a banner in the selected language, falling back to EN.
 *
 * @param {Banner} banner - The banner to read from.
 * @param {string} field - One of the banner text fields (heading, description, footer, buttons).
 * @param {string} languageCode - Selected language code, for example `'DE'`.
 * @returns {string} The text, or an empty string when neither language has it.
 */
export const getBannerText = (banner, field, languageCode) =>
  banner?.[languageCode]?.[field] || banner?.EN?.[field] || ''

/**
 * Tells whether a banner has anything to show: any text in the selected language (EN fallback)
 * or an image for the current viewport. This is the same rule BannerV1 has always applied
 * per slide, so an "empty" banner is skipped rather than rendered blank.
 *
 * @param {Banner} banner - The banner to check.
 * @param {string} languageCode - Selected language code.
 * @param {boolean} isMobile - `true` when the viewport is at or below the mobile breakpoint.
 * @returns {boolean} `true` when the banner can be displayed.
 */
export const isBannerEligible = (banner, languageCode, isMobile) =>
  TEXT_FIELDS.some((field) => Boolean(getBannerText(banner, field, languageCode))) ||
  Boolean(getBannerAsset(banner, 'image', isMobile))

/**
 * @param {Banner} banner
 * @returns {boolean} `true` when the banner belongs to the Default set (no category assigned).
 */
const isDefaultBanner = (banner) =>
  banner?.casinoCategoryId === undefined || banner?.casinoCategoryId === null || banner?.casinoCategoryId === ''

/**
 * Picks the banners to render for a page.
 *
 * Non-casino pages get their section unchanged. Casino pages get the banners assigned to the
 * current category when at least one of them is eligible; otherwise, and when the category is
 * unknown, they get the Default set. Category ids are compared numerically so a string id from
 * the route and a numeric id from the API match.
 *
 * @param {Object} params
 * @param {BannerSections} [params.bannerUrl] - All banner sections from the casino category endpoint.
 * @param {string} params.pageType - Page type, for example `'home'` or `'casino'` (case-insensitive).
 * @param {number|string} [params.categoryId] - Current main casino category id, casino pages only.
 * @param {string} params.languageCode - Selected language code.
 * @param {boolean} params.isMobile - Whether the viewport is mobile.
 * @returns {Banner[]} Banners to render, in stored order. Empty when the section is missing.
 */
export const resolveBanners = ({ bannerUrl, pageType, categoryId, languageCode, isMobile }) => {
  const section = bannerUrl?.[pageType?.toUpperCase()]?.banners || []
  if (pageType !== CATEGORY_BANNER_PAGE) return section

  const defaultBanners = section.filter(isDefaultBanner)
  if (!categoryId) return defaultBanners

  const categoryBanners = section.filter((banner) => Number(banner?.casinoCategoryId) === Number(categoryId))
  const hasEligibleCategoryBanner = categoryBanners.some((banner) => isBannerEligible(banner, languageCode, isMobile))

  return hasEligibleCategoryBanner ? categoryBanners : defaultBanners
}
